// Ο πίνακας δεξιά στον editor κάτοψης: ό,τι αφορά το επιλεγμένο (τραπέζι, περιοχή,
// άνοιγμα, σταθερό στοιχείο), η παλέτα «Πρόσθεσε», ή η εικόνα όλου του χώρου.
// Ίδιες επιλογές με τη Sala (xentrix_table src/screens/floor/Inspector.tsx).

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Circle, Columns2, Copy, DoorOpen, Flower2, Link2, RectangleHorizontal, RotateCw, Square, SquareDashed, Trash2, Type, Wine, X, Grid2x2,
} from 'lucide-react';
import { m, roomAt, type FloorApi, type FloorDoc, type Issue, type Room } from '../../../utils/salaFloor/floor';
import {
  addFixture, addOpening, addRoom, addTable, comboLabel, duplicateTable, patchFixture, patchOpening, patchRoom, patchTable,
  removeFixture, removeOpening, removeRoom, removeTable, rotateTable, tablesIn, unlink,
} from '../../../utils/salaFloor/floorEdit';
import { Btn, Field, Group, Heading, Note, Row, Segmented, Stepper, Toggle, inputClass } from './ui';

export type Sel = null | { k: 't'; key: string } | { k: 'room'; room: string } | { k: 'op'; room: string; id: string } | { k: 'fx'; id: string };
export type Mode = null | 'add' | 'link';

function Tile({ icon, label, sub, onClick }: { icon: React.ReactNode; label: string; sub: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="flex min-h-[56px] items-center gap-2.5 rounded-xl border border-line bg-surface-1 px-3 py-2 text-left shadow-sm transition-colors hover:border-line-strong active:scale-[0.98]">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface-3 text-t2">{icon}</span>
      <span className="grid min-w-0 gap-px"><span className="text-[13.5px] font-semibold text-t1">{label}</span><span className="text-[12px] text-t3">{sub}</span></span>
    </button>
  );
}

/** Το κείμενο ενός ελέγχου στη γλώσσα του ταμείου (ο κοινός κώδικας δίνει κωδικό). */
export function useIssueText() {
  const { t } = useTranslation(['floorEditor']);
  return (i: Issue) => t(`floorEditor:issues.${i.code}`, i.vars);
}

export default function Inspector({ doc, api, sel, mode, area, issues, onChange, onSelect, onMode, onArea, toast }: {
  doc: FloorDoc; api: FloorApi; sel: Sel; mode: Mode; area: string; issues: Issue[];
  onChange: (d: FloorDoc, key?: string | null) => void; onSelect: (s: Sel) => void; onMode: (m: Mode) => void; onArea: (a: string) => void;
  toast: (type: 'success' | 'error' | 'warning' | 'info', text: string) => void;
}) {
  const { t, i18n } = useTranslation(['floorEditor']);
  const k = (key: string, vars?: Record<string, unknown>) => String(t(`floorEditor:${key}`, vars as never));
  const lang = i18n.language?.startsWith('en') ? 'en' : 'el';
  const meters = (u: number) => m(u, lang);
  const issueText = useIssueText();
  const store = api.store;
  const prefix = k('defaults.tablePrefix');
  const combo = (tables: string[]) => comboLabel(doc, tables, k('defaults.upTo'), prefix);
  const seats = (n: number) => k('common.seats', { count: n });
  const TYPE_L = { entrance: k('add.entrance'), door: k('add.door'), window: k('add.window') } as const;
  const SIDE_L = { top: k('opening.sides.top'), right: k('opening.sides.right'), bottom: k('opening.sides.bottom'), left: k('opening.sides.left') } as const;
  const FIX_L = { bar: k('add.bar'), column: k('add.column'), plant: k('add.plant'), label: k('add.label') } as const;

  const target = (): Room => {
    if (area !== 'all') { const r = doc.rooms.find(x => x.key === area); if (r) return r; }
    if (sel && (sel.k === 'room' || sel.k === 'op')) { const r = doc.rooms.find(x => x.key === sel.room); if (r) return r; }
    if (sel?.k === 't') { const tb = doc.tables.find(x => x.key === sel.key); const r = tb && roomAt(doc.rooms, tb.x, tb.y); if (r) return r; }
    return doc.rooms[0];
  };

  let title = k('space.title');
  let sub = `${k('common.rooms', { count: doc.rooms.length })} · ${k('common.tables', { count: doc.tables.length })} · ${seats(doc.tables.reduce((s, x) => s + x.cap, 0))}`;
  let body: React.ReactNode;
  const closable = !!sel || !!mode;

  if (mode === 'add' && doc.rooms.length) {
    const room = target();
    title = k('add.title');
    sub = k('add.sub', { room: room.name.trim() || k('common.noName') });
    const tbl = (shape: 'round' | 'square' | 'rectangle', cap: number) => () => {
      const out = addTable(doc, room, shape, cap);
      onChange(out.doc);
      onSelect({ k: 't', key: out.key });
      toast('info', k('add.added', { n: out.n }));
    };
    const op = (type: 'entrance' | 'door' | 'window') => () => {
      const out = addOpening(doc, room, type, k('defaults.entranceLabel'));
      onChange(out.doc);
      onSelect({ k: 'op', room: room.key, id: out.id });
    };
    const fx = (type: 'bar' | 'column' | 'plant' | 'label') => () => {
      const out = addFixture(doc, room, type, type === 'bar' ? k('defaults.barLabel') : type === 'label' ? k('defaults.labelLabel') : '');
      onChange(out.doc);
      onSelect({ k: 'fx', id: out.key });
    };
    body = (
      <>
        <Heading>{k('add.tables')}</Heading>
        <div className="grid grid-cols-2 gap-2">
          <Tile icon={<Circle className="h-5 w-5" />} label={k('add.round')} sub={seats(2)} onClick={tbl('round', 2)} />
          <Tile icon={<Circle className="h-5 w-5" />} label={k('add.round')} sub={seats(4)} onClick={tbl('round', 4)} />
          <Tile icon={<Square className="h-5 w-5" />} label={k('add.square')} sub={seats(2)} onClick={tbl('square', 2)} />
          <Tile icon={<Square className="h-5 w-5" />} label={k('add.square')} sub={seats(4)} onClick={tbl('square', 4)} />
          <Tile icon={<RectangleHorizontal className="h-5 w-5" />} label={k('add.rect')} sub={seats(6)} onClick={tbl('rectangle', 6)} />
          <Tile icon={<RectangleHorizontal className="h-5 w-5" />} label={k('add.rect')} sub={seats(8)} onClick={tbl('rectangle', 8)} />
        </div>
        <Heading>{k('add.space')}</Heading>
        <div className="grid grid-cols-2 gap-2">
          <Tile icon={<DoorOpen className="h-5 w-5" />} label={k('add.entrance')} sub={k('add.entranceSub')} onClick={op('entrance')} />
          <Tile icon={<DoorOpen className="h-5 w-5" />} label={k('add.door')} sub={k('add.doorSub')} onClick={op('door')} />
          <Tile icon={<Grid2x2 className="h-5 w-5" />} label={k('add.window')} sub={k('add.windowSub')} onClick={op('window')} />
          <Tile icon={<Wine className="h-5 w-5" />} label={k('add.bar')} sub={k('add.barSub')} onClick={fx('bar')} />
          <Tile icon={<Columns2 className="h-5 w-5" />} label={k('add.column')} sub={k('add.columnSub')} onClick={fx('column')} />
          <Tile icon={<Flower2 className="h-5 w-5" />} label={k('add.plant')} sub={k('add.plantSub')} onClick={fx('plant')} />
          <Tile icon={<Type className="h-5 w-5" />} label={k('add.label')} sub={k('add.labelSub')} onClick={fx('label')} />
          <Tile icon={<SquareDashed className="h-5 w-5" />} label={k('add.room')} sub={k('add.roomSub')} onClick={() => {
            const out = addRoom(doc, k('defaults.newRoom'));
            onChange(out.doc);
            onArea('all');
            onSelect({ k: 'room', room: out.key });
          }} />
        </div>
        <Note>{k('add.note')}</Note>
      </>
    );
  } else if (sel?.k === 't') {
    const tb = doc.tables.find(x => x.key === sel.key);
    if (!tb) return null;
    const room = roomAt(doc.rooms, tb.x, tb.y);
    const dup = doc.tables.some(o => o.key !== tb.key && o.n.trim() === tb.n.trim());
    const set = (patch: Parameters<typeof patchTable>[2], key?: string) => onChange(patchTable(doc, tb.key, patch), key);
    const mine = doc.combos.filter(c => c.tables.includes(tb.key));
    const maxCap = tb.shape === 'rectangle' ? 12 : 6;
    title = mode === 'link' ? k('table.linkTitle') : k('table.title', { n: tb.n || '?' });
    sub = mode === 'link' ? k('table.linkSub', { n: tb.n }) : `${room ? room.name : k('common.outsideRooms')} · ${seats(tb.cap)}`;
    body = mode === 'link' ? (
      <>
        <Note>{k('table.linkNote', { n: tb.n })}</Note>
        <Btn icon={<X className="h-4 w-4" />} onClick={() => onMode(null)}>{k('common.cancel')}</Btn>
      </>
    ) : (
      <>
        <Field label={k('table.number')} error={!tb.n.trim() ? k('table.numberMissing') : dup ? k('table.numberDup', { n: tb.n }) : null}>
          <input className={`${inputClass} text-[16px] font-bold ${dup || !tb.n.trim() ? 'border-red-500' : ''}`} value={tb.n}
            onChange={e => set({ n: e.target.value.replace(/\s+/g, '').slice(0, 20) }, `n:${tb.key}`)} />
        </Field>
        <Group label={k('table.shape')}>
          <Segmented value={tb.shape} onChange={shape => set({ shape, cap: shape === 'rectangle' ? Math.max(4, tb.cap) : Math.min(tb.cap, 6) })}
            options={[{ value: 'round', label: k('add.round') }, { value: 'square', label: k('add.square') }, { value: 'rectangle', label: k('add.rect') }]} />
        </Group>
        <Row label={k('table.seats')} hint={k('table.seatsHint')}>
          <Stepper value={tb.cap} min={1} max={maxCap} onChange={v => set({ cap: v }, `cap:${tb.key}`)} less={k('common.less')} more={k('common.more')} />
        </Row>
        <Field label={k('table.name')} hint={k('table.nameHint')}>
          <input className={inputClass} value={tb.name || ''} placeholder="—" onChange={e => set({ name: e.target.value.slice(0, 50) || null }, `name:${tb.key}`)} />
        </Field>

        {store.reservations && (
          <>
            <Heading>{k('table.reservations')}</Heading>
            <Toggle checked={tb.res} onChange={v => set({ res: v })} label={k('table.reservable')} hint={tb.res ? undefined : k('table.walkInOnly')} />
            <Toggle checked={tb.res && tb.online} disabled={!tb.res} onChange={v => set({ online: v })} label={k('table.online')}
              hint={tb.res && !tb.online ? k('table.onlineOff') : undefined} />
            {tb.res && tb.online && (
              <Row label={k('table.minOnline')} hint={k('table.minOnlineHint')}>
                <Stepper value={Math.min(tb.minOnline, tb.cap)} min={1} max={tb.cap} onChange={v => set({ minOnline: v }, `min:${tb.key}`)} less={k('common.less')} more={k('common.more')} />
              </Row>
            )}
            {tb.res && (
              <Group label={k('table.priority')}>
                <Segmented value={tb.prio > 0 ? 10 : tb.prio < 0 ? -10 : 0} onChange={v => set({ prio: v })}
                  options={[{ value: 10, label: k('table.first') }, { value: 0, label: k('table.normal') }, { value: -10, label: k('table.last') }]} />
              </Group>
            )}
            <Heading sub={k('table.joinsSub')}>{k('table.joins')}</Heading>
            <div className="flex flex-wrap gap-1.5">
              {mine.map(c => (
                <span key={c.key} className="inline-flex h-9 items-center gap-1 rounded-full border border-line bg-surface-2 pl-3 pr-0.5 text-[13px] font-semibold text-t1">
                  {combo(c.tables)}
                  <button type="button" aria-label={k('table.unjoin', { label: combo(c.tables) })} onClick={() => onChange(unlink(doc, c.key))}
                    className="grid h-8 w-8 place-items-center rounded-full text-t3 hover:text-t1"><X className="h-3.5 w-3.5" /></button>
                </span>
              ))}
              <button type="button" onClick={() => onMode('link')}
                className="inline-flex h-9 items-center gap-1.5 rounded-full border border-dashed border-line-strong px-3 text-[13px] font-semibold text-t2 hover:text-t1">
                <Link2 className="h-4 w-4" /> {k('table.joinAction')}
              </button>
            </div>
          </>
        )}

        {tb.inUse ? (
          <Note tone="warn">{k('table.inUse')}</Note>
        ) : tb.upcoming.length > 0 && (
          <Note tone="warn">{k('table.upcoming', { list: tb.upcoming.map(u => `${u.name}, ${u.time}`).join(' · ') })}</Note>
        )}
        <div className="flex flex-wrap gap-1.5">
          <Btn size="sm" icon={<Copy className="h-4 w-4" />} onClick={() => {
            const out = duplicateTable(doc, tb.key);
            if (out) { onChange(out.doc); onSelect({ k: 't', key: out.key }); toast('info', k('table.duplicated', { n: out.n })); }
          }}>{k('table.duplicate')}</Btn>
          <Btn size="sm" icon={<RotateCw className="h-4 w-4" />} disabled={tb.shape !== 'rectangle'} onClick={() => onChange(rotateTable(doc, tb.key))}>{k('common.rotate')}</Btn>
          <Btn size="sm" variant="danger" icon={<Trash2 className="h-4 w-4" />} disabled={tb.inUse} onClick={() => {
            onChange(removeTable(doc, tb.key));
            onSelect(null);
            toast('info', k('table.removed', { n: tb.n }));
          }}>{k('common.remove')}</Btn>
        </div>
        {tb.hasHistory && <Note>{k('table.history')}</Note>}
      </>
    );
  } else if (sel?.k === 'op') {
    const room = doc.rooms.find(r => r.key === sel.room);
    const o = room?.openings.find(x => x.id === sel.id);
    if (!room || !o) return null;
    const set = (patch: Parameters<typeof patchOpening>[3], key?: string) => onChange(patchOpening(doc, room.key, o.id, patch), key);
    const span = o.side === 'top' || o.side === 'bottom' ? room.w : room.h;
    title = `${TYPE_L[o.type]}${o.label ? ` · ${o.label}` : ''}`;
    sub = `${room.name} · ${SIDE_L[o.side]}`;
    body = (
      <>
        <Group label={k('opening.type')}>
          <Segmented value={o.type} onChange={type => set({ type, label: type === 'entrance' && !o.label ? k('defaults.entranceLabel') : o.label })}
            options={[{ value: 'entrance', label: TYPE_L.entrance }, { value: 'door', label: TYPE_L.door }, { value: 'window', label: TYPE_L.window }]} />
        </Group>
        <Field label={k('opening.label')} hint={k('opening.labelHint')}>
          <input className={inputClass} value={o.label} onChange={e => set({ label: e.target.value.slice(0, 18) }, `lab:${o.id}`)} />
        </Field>
        <Row label={k('opening.width')}>
          <Stepper value={o.len} display={meters(o.len)} step={10} min={30} max={span - o.at - 8} onChange={len => set({ len }, `len:${o.id}`)} less={k('common.less')} more={k('common.more')} />
        </Row>
        <Group label={k('opening.wall')}>
          <Segmented value={o.side} onChange={side => set({ side, at: 20 })}
            options={[{ value: 'top', label: k('opening.short.top') }, { value: 'right', label: k('opening.short.right') }, { value: 'bottom', label: k('opening.short.bottom') }, { value: 'left', label: k('opening.short.left') }]} />
        </Group>
        <Note>{k('opening.dragNote')}</Note>
        <div><Btn size="sm" variant="danger" icon={<Trash2 className="h-4 w-4" />} onClick={() => { onChange(removeOpening(doc, room.key, o.id)); onSelect({ k: 'room', room: room.key }); }}>{k('common.remove')}</Btn></div>
      </>
    );
  } else if (sel?.k === 'room') {
    const r = doc.rooms.find(x => x.key === sel.room);
    if (!r) return null;
    const set = (patch: Partial<Room>, key?: string) => onChange(patchRoom(doc, r.key, patch), key);
    const tl = tablesIn(doc, r);
    const dupName = doc.rooms.some(o => o.key !== r.key && o.name.trim().toLocaleLowerCase('el') === r.name.trim().toLocaleLowerCase('el'));
    title = r.name.trim() || k('common.noName');
    sub = k('room.sub', { w: meters(r.w), h: meters(r.h) });
    body = (
      <>
        <Field label={k('room.name')} error={!r.name.trim() ? k('room.nameMissing') : dupName ? k('room.nameDup') : null}>
          <input className={`${inputClass} ${!r.name.trim() || dupName ? 'border-red-500' : ''}`} value={r.name} onChange={e => set({ name: e.target.value.slice(0, 40) }, `name:${r.key}`)} />
        </Field>
        <Group label={k('room.kind')}>
          <Segmented value={r.kind} onChange={kind => set({ kind })} options={[{ value: 'indoor', label: k('room.indoor') }, { value: 'outdoor', label: k('room.outdoor') }]} />
        </Group>
        <Row label={k('room.width')}>
          <Stepper value={r.w} display={meters(r.w)} step={25} min={175} onChange={w => set({ w }, `w:${r.key}`)} less={k('common.less')} more={k('common.more')} />
        </Row>
        <Row label={k('room.depth')} hint={k('room.depthHint')}>
          <Stepper value={r.h} display={meters(r.h)} step={25} min={125} onChange={h => set({ h }, `h:${r.key}`)} less={k('common.less')} more={k('common.more')} />
        </Row>
        {store.dine_in && (
          <Field label={k('room.printer')} hint={k('room.printerHint')}>
            <select className={inputClass} value={r.printerId ?? ''} onChange={e => set({ printerId: e.target.value ? Number(e.target.value) : null })}>
              <option value="">{k('room.printerDefault')}</option>
              {/* Λογαριασμός ζώνης = χαρτί προς τον πελάτη: μόνο εκτυπωτές αποδείξεων (14/10/2026). Server χωρίς
                  prints_receipts → όλοι, όπως πριν· η τωρινή επιλογή μένει ορατή ακόμη κι αν δεν είναι πια τέτοιος. */}
              {api.printers
                .filter(p => (p as { prints_receipts?: boolean }).prints_receipts !== false || p.id === r.printerId)
                .map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
        )}
        <Heading sub={r.openings.length}>{k('room.openings')}</Heading>
        {r.openings.length > 0 && (
          <ul className="grid">
            {r.openings.map(o => (
              <li key={o.id} className="flex items-center gap-1 border-b border-line">
                <button type="button" onClick={() => onSelect({ k: 'op', room: r.key, id: o.id })} className="grid min-w-0 flex-1 gap-px py-2 text-left">
                  <span className="text-[13.5px] font-semibold text-t1">{TYPE_L[o.type]}{o.label ? ` · ${o.label}` : ''}</span>
                  <span className="text-[12px] text-t3">{SIDE_L[o.side]} · {meters(o.len)}</span>
                </button>
                <Btn variant="ghost" size="icon" aria-label={`${k('common.remove')}: ${TYPE_L[o.type]}`} onClick={() => onChange(removeOpening(doc, r.key, o.id))}><Trash2 className="h-4 w-4" /></Btn>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-1.5">
          {(['entrance', 'door', 'window'] as const).map(type => (
            <Btn key={type} size="sm" icon={type === 'window' ? <Grid2x2 className="h-4 w-4" /> : <DoorOpen className="h-4 w-4" />}
              onClick={() => { const out = addOpening(doc, r, type, k('defaults.entranceLabel')); onChange(out.doc); onSelect({ k: 'op', room: r.key, id: out.id }); }}>{TYPE_L[type]}</Btn>
          ))}
        </div>
        <Heading sub={`${tl.length} · ${seats(tl.reduce((s, x) => s + x.cap, 0))}`}>{k('room.tablesHere')}</Heading>
        {tl.length ? (
          <Note>{k('room.moveNote')}</Note>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            <Btn size="sm" variant="primary" onClick={() => onMode('add')}>{k('room.addTables')}</Btn>
            <Btn size="sm" variant="danger" icon={<Trash2 className="h-4 w-4" />} disabled={doc.rooms.length <= 1}
              onClick={() => { onChange(removeRoom(doc, r.key)); onSelect(null); onArea('all'); }}>{k('room.removeRoom')}</Btn>
          </div>
        )}
      </>
    );
  } else if (sel?.k === 'fx') {
    const f = doc.fixtures.find(x => x.id === sel.id);
    if (!f) return null;
    const room = roomAt(doc.rooms, f.x + f.w / 2, f.y + f.h / 2);
    const set = (patch: Parameters<typeof patchFixture>[2], key?: string) => onChange(patchFixture(doc, f.id, patch), key);
    title = FIX_L[f.type];
    sub = room ? room.name : k('common.outsideRooms');
    const long = Math.max(f.w, f.h);
    body = (
      <>
        {(f.type === 'bar' || f.type === 'label') && (
          <Field label={k('fixture.text')}><input className={inputClass} value={f.label} onChange={e => set({ label: e.target.value.slice(0, 16) }, `flab:${f.id}`)} /></Field>
        )}
        {f.type === 'bar' && (
          <Row label={k('fixture.length')}>
            <Stepper value={long} display={meters(long)} step={20} min={60} onChange={v => set(f.h > f.w ? { h: v } : { w: v }, `fl:${f.id}`)} less={k('common.less')} more={k('common.more')} />
          </Row>
        )}
        {(f.type === 'plant' || f.type === 'column') && (
          <Row label={k('fixture.size')}>
            <Stepper value={f.w} display={meters(f.w)} step={4} min={16} max={80} onChange={v => set({ w: v, h: v }, `fs:${f.id}`)} less={k('common.less')} more={k('common.more')} />
          </Row>
        )}
        <Note>{k('fixture.note')}</Note>
        <div className="flex flex-wrap gap-1.5">
          {f.type === 'bar' && <Btn size="sm" icon={<RotateCw className="h-4 w-4" />} onClick={() => set({ w: f.h, h: f.w })}>{k('common.rotate')}</Btn>}
          <Btn size="sm" variant="danger" icon={<Trash2 className="h-4 w-4" />} onClick={() => { onChange(removeFixture(doc, f.id)); onSelect(null); }}>{k('common.remove')}</Btn>
        </div>
      </>
    );
  } else {
    body = (
      <>
        <div className="grid gap-2">
          {doc.rooms.map(r => {
            const tl = tablesIn(doc, r);
            return (
              <button key={r.key} type="button" onClick={() => onSelect({ k: 'room', room: r.key })}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 rounded-xl border border-line bg-surface-1 px-3.5 py-3 text-left shadow-sm hover:border-line-strong">
                <span className="text-[15px] font-semibold text-t1">{r.name.trim() || k('common.noName')}</span>
                <span className="text-[13px] font-semibold text-t1">{seats(tl.reduce((s, x) => s + x.cap, 0))}</span>
                <span className="text-[12px] text-t3">{r.kind === 'indoor' ? k('room.indoorShort') : k('room.outdoorShort')} · {meters(r.w)} × {meters(r.h)}</span>
                <span className="text-[12px] text-t3">{k('common.tables', { count: tl.length })}</span>
              </button>
            );
          })}
        </div>
        <Heading sub={issues.length || undefined}>{k('overview.check')}</Heading>
        {issues.length === 0
          ? <Note tone="ok">{k('overview.allGood')}</Note>
          : issues.map((i, n) => <Note key={n} tone={i.level === 'error' ? 'error' : 'warn'}>{issueText(i)}</Note>)}
        {store.reservations && (
          <>
            <Heading sub={doc.combos.length}>{k('overview.combos')}</Heading>
            {doc.combos.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {doc.combos.map(c => (
                  <span key={c.key} className="inline-flex h-9 items-center gap-1 rounded-full border border-line bg-surface-2 pl-3 pr-0.5 text-[13px] font-semibold text-t1">
                    {combo(c.tables)}
                    <button type="button" aria-label={k('table.unjoin', { label: combo(c.tables) })} onClick={() => onChange(unlink(doc, c.key))}
                      className="grid h-8 w-8 place-items-center rounded-full text-t3 hover:text-t1"><X className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
              </div>
            )}
            <Note>{k('overview.comboNote')}</Note>
          </>
        )}
        {store.reservations && <Note>{k('overview.shared')}</Note>}
      </>
    );
  }

  return (
    <aside className="flex w-[340px] shrink-0 flex-col border-l border-line bg-surface-1">
      <div className="flex items-start gap-2.5 border-b border-line px-4 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[20px] font-bold leading-tight text-t1">{title}</h2>
          <p className="mt-1 text-[12.5px] text-t3">{sub}</p>
        </div>
        {closable && <Btn size="icon" aria-label={k('common.close')} onClick={() => { onSelect(null); onMode(null); }}><X className="h-4 w-4" /></Btn>}
      </div>
      <div className="grid min-h-0 flex-1 auto-rows-max content-start gap-3.5 overflow-y-auto px-4 pb-6 pt-4">{body}</div>
    </aside>
  );
}
